
type EntityCustomView<M extends Record<string, unknown> = Record<string, unknown>> = { key: string; [key: string]: unknown };
import { getCollectionPathsCombinations, removeInitialAndTrailingSlashes } from "./navigation_utils";
import { getSubcollections } from "@rebasepro/common";
import type { AdminCollection } from "@rebasepro/cms-types";

export type NavigationViewInternal<M extends Record<string, unknown> = Record<string, unknown>> =
    | NavigationViewEntityInternal<M>
    | NavigationViewCollectionInternal<M>
    | NavigationViewEntityCustomInternal<M>;

export interface NavigationViewEntityInternal<M extends Record<string, unknown>> {
    type: "entity";
    entityId: string | number;
    slug: string;
    path: string;
    parentCollection: AdminCollection<M>;
}

export interface NavigationViewCollectionInternal<M extends Record<string, unknown>> {
    type: "collection";
    id: string;
    slug: string;
    path: string;
    collection: AdminCollection<M>;
}

export interface NavigationViewEntityCustomInternal<M extends Record<string, unknown>> {
    type: "custom_view";
    slug: string;
    path: string;
    entityId: string | number;
    view: EntityCustomView<M>;
}

export function getNavigationEntriesFromPath(props: {
    path: string,
    collections: AdminCollection[] | undefined,
    currentFullPath?: string,
    contextEntityViews?: EntityCustomView[]
}): NavigationViewInternal[] {

    const {
        path,
        collections = [],
        currentFullPath
    } = props;

    const subpaths = removeInitialAndTrailingSlashes(path).split("/");
    const subpathCombinations = getCollectionPathsCombinations(subpaths);

    const result: NavigationViewInternal[] = [];
    for (let i = 0; i < subpathCombinations.length; i++) {
        const subpathCombination = subpathCombinations[i];

        const collection = collections && collections.find((entry) => entry.slug === subpathCombination);

        if (collection) {
            const collectionPath = currentFullPath && currentFullPath.length > 0
                ? (currentFullPath + "/" + collection.slug)
                : collection.slug;
            result.push({
                type: "collection",
                id: collection.slug,
                slug: collectionPath,
                path: collectionPath,
                collection
            });
            const restOfThePath = removeInitialAndTrailingSlashes(removeInitialAndTrailingSlashes(path).replace(subpathCombination, ""));
            const nextSegments = restOfThePath.length > 0 ? restOfThePath.split("/") : [];
            if (nextSegments.length > 0) {
                const entityId = nextSegments[0];
                const path = collectionPath + "/" + entityId;
                result.push({
                    type: "entity",
                    entityId,
                    slug: collectionPath,
                    path,
                    parentCollection: collection
                });
                if (nextSegments.length > 1) {
                    const newPath = nextSegments.slice(1).join("/");
                    if (!collection) {
                        throw Error("collection not found resolving path: " + collection);
                    }
                    const entityViews = collection.entityViews;
                    const customView = entityViews && entityViews
                        .map((entry) => resolveEntityView(entry, props.contextEntityViews))
                        .filter((v): v is EntityCustomView => v != null)
                        .find((entry) => entry.key === newPath);
                    const subcollections = getSubcollections(collection);
                    if (customView) {
                        result.push({
                            type: "custom_view",
                            slug: collectionPath,
                            entityId: entityId,
                            path: path + "/" + customView.key,
                            view: customView
                        });
                    } else if (subcollections) {
                        result.push(...getNavigationEntriesFromPath({
                            path: newPath,
                            collections: subcollections,
                            currentFullPath: path,
                            contextEntityViews: props.contextEntityViews
                        }));
                    }
                }
            }
            break;
        }

    }
    return result;
}

function resolveEntityView(entityView: string | EntityCustomView, contextEntityViews?: EntityCustomView[]): EntityCustomView | undefined {
    if (typeof entityView === "string") {
        return contextEntityViews?.find((entry) => entry.key === entityView);
    } else {
        return entityView;
    }
}
